import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useContext, useEffect, useState } from 'react';
import { ClickAwayListener, Loading, TextField } from '@wayste/sour-ui';
import { Popover } from '@headlessui/react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { debounce } from 'lodash';
import { Controller, get, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { CustomFormContext } from '../formWrapper/SourSearchWrapper';
import { SearchTips } from './components/helper/SearchTips';
import { NoResults } from './components/helper/noResults';
import { SearchResults } from './searchResults';
const TIMEOUT = 300;
///////////////////////////////////////////////
// SOUR SEARCH BASE COMPONENT
///////////////////////////////////////////////
export const SourSearch = forwardRef(({ value, options }, ref) => {
    useEffect(() => {
        if (ref && 'current' in ref && ref.current) {
            ref.current.focus();
        }
    }, [ref]); // Dependency on inputRef so that focus is set correctly
    // DEFAULT OPTIONS
    const { showResults = true, showTips = true, showMessages = true, searchPopoverFixed = false, resultSize = undefined, placeholder = 'Search', searchPositionFixed = false, highlight = false, label = 'Search', textFieldStyle, textFieldDense, onMagnifyingGlassClick, hideClearButton, } = options || {
        showResults: true,
        showTips: true,
        showMessages: true,
        searchPopoverFixed: false,
        resultSize: undefined,
        placeholder: 'Search',
        searchPositionFixed: false,
        highlight: false,
        label: 'Search',
        onMagnifyingGlassClick: undefined,
        textFieldStyle: undefined,
        textFieldDense: false,
        hideClearButton: false,
    };
    // LOCAL STATE
    const [isOpen, setIsOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isHidden, setIsHidden] = useState(false);
    const [search, setSearch] = useState('');
    // const [restricted, setRestricted] = useState<string[]>([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [filteredItemsType, setFilteredItemsType] = useState(undefined);
    const [initialFormChecked, setInitialFormChecked] = useState(false);
    const [typing, setTyping] = useState(false);
    //REACT HOOK FORM CONTEXT
    const { onSubmit, results, isLoading, setSearchActive, formInitialized, resetValues } = useContext(CustomFormContext);
    const { handleSubmit, setValue, control, watch, formState: { errors }, } = useFormContext();
    const watchQuery = watch('query');
    ///////////////////////////////////////////////
    // FUNCTIONS
    ///////////////////////////////////////////////
    // HANDLE SEARCH SUBMIT KEYBOARD ENTER
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };
    // HANDLE SEARCH INPUT CHANGE
    const handleInputChange = (event) => {
        const searchValue = event.target.value;
        setTyping(true);
        setIsHidden(false);
        setSearch(searchValue);
        setIsOpen(true);
        if (searchValue)
            setSearchActive(true);
        if (searchValue.length === 0) {
            setValue('query', { query: '', restrictToFields: [] });
            setSearchActive(false);
        }
    };
    // HANDLE CLEARING INPUT WITH X BUTTON
    const handleClearInput = () => {
        setValue('query', { query: '', restrictToFields: [] });
        setSearch('');
        setSearchActive(false);
    };
    // HANDLE SEARCH IN FOCUS
    const handleFocus = () => {
        setIsFocused(true);
        setIsHidden(false);
        setIsOpen(true);
    };
    // HANDLE SEARCH OUT OF FOCUS
    const handleBlur = () => {
        setIsFocused(false);
    };
    // HANDLE CLICKING OUTSIDE OF INPUT OR POPOVER
    const handleOnClickAway = () => {
        setIsHidden(true);
    };
    ///////////////////////////////////////////////
    // HOOKS
    ///////////////////////////////////////////////
    useEffect(() => {
        if (results?.aggregations?.index_agg) {
            setFilteredItemsType('aggregate');
            // Incomplete types for aggregations in elastic search
            setFilteredItems((results?.aggregations?.index_agg).buckets);
        }
        else {
            if (results?.hits?.hits) {
                setFilteredItemsType('general');
                setFilteredItems(results.hits.hits);
            }
        }
    }, [results]);
    useEffect(() => {
        setSearch(value || '');
    }, [value]);
    const handleSearch = debounce(() => {
        if (!initialFormChecked || resetValues === 'all' || resetValues === 'search')
            return;
        setValue('query.query', search);
        handleSubmit(onSubmit)();
        setTyping(false);
    }, TIMEOUT);
    useEffect(() => {
        handleSearch();
        return () => handleSearch.cancel();
    }, [search]);
    // SETS FIRST VALUE FROM DEFAULT FILTERS OR QUERY PARAMS
    useEffect(() => {
        if (formInitialized) {
            if (watchQuery?.query)
                setSearch(watchQuery.query);
            // TIMEOUT NEEDED TO PREVENT CHANGING INITIAL VALUES
            setTimeout(() => {
                setInitialFormChecked(true);
            }, 50);
        }
    }, [formInitialized]);
    useEffect(() => {
        if (resetValues === 'all' || resetValues === 'search')
            setSearch('');
    }, [resetValues]);
    // // NEED FOR OPTIONS TO REFRESH
    useEffect(() => {
        // console.log('OPTIONS', options);
    }, [options]);
    ///////////////////////////////////////////////
    // RENDER
    ///////////////////////////////////////////////
    return (_jsx(ClickAwayListener, { onClickAway: handleOnClickAway, children: _jsxs("div", { className: "relative w-full", children: [_jsx(Controller, { name: `query.query`, control: control, defaultValue: '', render: ({ field }) => (_jsx(TextField, { placeholder: placeholder, label: label, error: get(errors), startAdornment: typing && showMessages ? (_jsx(Loading, { size: "h-4 w-4", className: "!text-sourgum-greyblue-400" })) : (_jsx(MagnifyingGlassIcon, { className: `${textFieldDense ? 'size-4' : 'size-5'} text-black ${onMagnifyingGlassClick ? 'cursor-pointer' : ''}`, "aria-hidden": "true", onClick: () => {
                                if (onMagnifyingGlassClick)
                                    onMagnifyingGlassClick();
                            } })), endAdornment: search.length > 0 &&
                            !hideClearButton && (_jsx(_Fragment, { children: _jsx("div", { onClick: handleClearInput, children: _jsx(XMarkIcon, { className: `size-5 cursor-pointer text-black`, "aria-hidden": "true" }) }) })), dense: textFieldDense, inputProps: {
                            ref: ref,
                            value: search,
                            style: textFieldStyle ? textFieldStyle : { background: 'white' },
                            onKeyPress: (event) => {
                                handleKeyPress(event);
                            },
                            onChange: (event) => {
                                field.onChange(event);
                                handleInputChange(event);
                            },
                            onFocus: () => {
                                handleFocus();
                            },
                            onBlur: () => {
                                handleBlur();
                            },
                        } })) }), _jsx(Popover, { className: twMerge(`absolute z-10 ${searchPopoverFixed ? '' : 'w-full'}`, options?.popoverClassName), children: _jsx("div", { style: { display: `${isOpen ? 'block' : 'none'}` }, className: `${searchPositionFixed ? 'fixed' : ''}`, children: _jsxs(Popover.Panel, { className: `z-10 my-2 max-h-80 w-full overflow-auto overflow-x-hidden rounded bg-white text-base shadow-lg focus:outline-none`, static: true, children: [showTips && isFocused && search === '' && !typing ? _jsx(SearchTips, {}) : _jsx(_Fragment, {}), showMessages &&
                                    isFocused &&
                                    search.length > 0 &&
                                    filteredItems.length === 0 &&
                                    isLoading === false &&
                                    !typing ? (_jsx(NoResults, { search: search })) : (_jsx(_Fragment, {})), showResults && !isHidden && filteredItems.length > 0 && search.length > 0 && !typing ? (_jsx(SearchResults, { size: options?.resultSize, hits: filteredItems, hitsType: filteredItemsType, search: search })) : (_jsx(_Fragment, {}))] }) }) })] }) }));
});
SourSearch.displayName = 'SourSearch';
//# sourceMappingURL=search.js.map